import React, { useState, useMemo, useEffect, useCallback } from 'react';
import styles from './CardRowWithClips.module.scss';
import Highlighter from 'react-highlight-words';
import Avatar from '../Avatar/Avatar';
import Col from '../GridSystem/Col';
import format from 'date-fns/format';
import { Card } from '../Card';
import CardMeta from '../Card/CardMeta';
import CardMetaContent from '../Card/CardMetaContent';
import NumberCheckbox from '../NumberCheckbox/NumberCheckbox';
import cx from 'classnames';
import { useClipsContext } from '../../context/ClipsContext';
import { Icon } from '../Icon';
import ClipCard from '../ClipCard/ClipCard';
import VideoService from '../../service/VideoService';
import ScrollContainer from '../ScrollContainer/ScrollContainer';
import Spinner from '../../../common/components/Spinner';
import { ScenesType } from '../../../common/apis/CmsAPI';

const CardRowWithClips = ({ video, videoIndex, searchInput }) => {
  const { userFullName, userAvatar, userFirstLetter } = useMemo(() => {
    if (video.author) {
      const fullName = video.author.name;
      const avatar = video.author.avatar;
      return { userFullName: fullName, userAvatar: avatar, userFirstLetter: fullName.charAt(0).toUpperCase() };
    }
    return {};
  }, [video.author]);

  const { selectedClips, toggleSelectedClip, videosWithClips } = useClipsContext();

  const [showScenes, setShowScenes] = useState(false);
  const [scenes, setScenes] = useState(null);
  const [loadingScenes, setLoadingScenes] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);

  const toggleShowScenes = useCallback(() => {
    setShowScenes(prevState => !prevState);
  }, [setShowScenes]);

  const handleSelectScene = sceneIndex => {
    toggleSelectedClip(video.folder, video.id, {
      ...scenes[sceneIndex],
      author: video.author,
      storyId: video.id,
      storyOrientation: video.orientation,
    });
  };

  const onResourceGenerated = useCallback(
    (jobId, jobDetails) => {
      if (currentJobId === jobId) setCurrentJobId(null);
      setScenes(prevState =>
        prevState.map(item => {
          if (item.id === jobDetails.outputs[0].relatedTo.id) {
            console.log('will ');
            const { url, type, label, finished, properties } = jobDetails.outputs[0];
            return { ...item, resources: [{ url, type, label, created: finished, properties }] };
          } else {
            return item;
          }
        })
      );
    },
    [scenes, setScenes]
  );

  useEffect(() => {
    const getScenes = async () => {
      try {
        setLoadingScenes(true);
        let videoDetails = {};
        videoDetails = await VideoService.getVideoDetails(video.id, ScenesType.REGULAR);
        const sortedScenes = [...videoDetails.scenes].sort((a, b) => a.sort - b.sort);
        setScenes(sortedScenes);
        setLoadingScenes(false);
      } catch (e) {
        console.log('Error');
      }
    };

    if (showScenes && !scenes) getScenes();
  }, [showScenes]);

  return (
    <Col key={video.id} xs={12} className={styles.listCol}>
      <Card
        id={videoIndex}
        data-cy="video"
        onClick={toggleShowScenes}
        className={cx(styles.card, { [styles.activeCard]: videosWithClips[video.id] })}
        listMode
      >
        <div className={styles.cardHeader}>
          {videosWithClips[video.id] ? (
            <NumberCheckbox number={videosWithClips[video.id]} />
          ) : (
            <Avatar image={userAvatar || undefined} title={userFullName} size="small" alt="Click to show clips" />
          )}
        </div>
        <div className={styles.titleContainer}>
          {/* <CardTitle> */}
          <Highlighter
            highlightClassName="wordHighlighter"
            className={styles.titleList}
            searchWords={searchInput?.trim().length > 0 ? [searchInput] : ['']}
            autoEscape
            textToHighlight={video.name}
          />
          {/* </CardTitle> */}
        </div>
        <div className={styles.metaContainer}>
          <CardMeta>
            <CardMetaContent>
              <div>
                <div className={styles.username}>
                  <span>{userFullName}</span>
                </div>
              </div>
            </CardMetaContent>
          </CardMeta>
        </div>
        <div className={styles.listDate}>
          <span>{format(new Date(`${video.createDate}`), 'dd MMM yyyy')}</span>
        </div>
        <div className={styles.clips}></div>
        <div className={styles.arrowContainer}>
          <Icon src="showHideArrow" className={cx(styles.arrowIcon, { [styles.turnArrow]: showScenes })} />
        </div>
      </Card>
      {showScenes && (
        <div className={styles.scenesContainer}>
          {loadingScenes && (
            <div className={styles.loadingContainer}>
              <Spinner width={'40px'} height={'40px'} size={'2px'} />
            </div>
          )}
          {!loadingScenes && scenes && (
            <ScrollContainer className={styles.clipsContainer}>
              {scenes.map((scene, index) => (
                <ClipCard
                  videoId={video.id}
                  key={scene.id}
                  itemId={scene.id}
                  clip={scene}
                  author={video.author}
                  clipIndex={index}
                  handleSelectClip={handleSelectScene}
                  selectedClip={selectedClips[scene.id]}
                  showCheckbox={true}
                  onResourceGenerated={onResourceGenerated}
                  currentJobId={currentJobId}
                  updateJobId={setCurrentJobId}
                  className={cx(styles.clipCard, { [styles.lastClipCard]: index === scenes.length - 1 })}
                />
              ))}
            </ScrollContainer>
          )}
        </div>
      )}
    </Col>
  );
};

export default CardRowWithClips;
